/***************************************************************************************
 * FileName      : ViewpointPresetService.ts
 * Description   : 뷰포인트 프리셋 적용 서비스
 *                 - Matterport SDK를 통한 카메라 방향(rotation) 및 줌(zoom) 설정
 *                 - 장비별 Sweep 이동 후 뷰포인트 프리셋 자동 적용
 *                 - 08-viewpoint-preset 프로젝트의 카메라 제어 로직 기반
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.11
 * Modified Date : 2026.02.11 모든 디버깅 로그 제거
 * Reference     : matterport_mvp1/src/08-viewpoint-preset, packages/scjeil-smart_ict_lab-app/assets/json/matterport/*.json
 ***************************************************************************************/

// 2026.02.11 : 박은정 : 장비 데이터 서비스에서 뷰포인트 프리셋 타입 import
import { EquipmentDataService, ViewpointPreset } from './EquipmentDataService';

// ============================================================================
// 뷰포인트 프리셋 적용 옵션 인터페이스
// ============================================================================

// 2026.02.11 : 박은정 : 뷰포인트 프리셋 적용 시 전환 옵션
export interface ViewpointApplyOptions {
  /** Sweep 이동 전환 시간 (ms, 기본값 1500) */
  sweepTransitionTime?: number;
  /** 카메라 회전 속도 (도/초, 기본값 120) */
  rotationSpeed?: number;
  /** Sweep 이동 전환 방식 (기본값 FLY) */
  sweepTransition?: string;
}

// 2026.02.11 : 박은정 : 기본 전환 옵션 상수
const DEFAULT_OPTIONS: Required<ViewpointApplyOptions> = {
  sweepTransitionTime: 1500,
  rotationSpeed: 120,
  sweepTransition: 'FLY',
};

// ============================================================================
// ViewpointPresetService 클래스
// ============================================================================

/**
 * 2026.02.11 : 박은정 : 뷰포인트 프리셋 적용 서비스 (싱글톤)
 * - Matterport SDK를 통해 Sweep 이동 → 카메라 회전 → 줌 설정을 순차 실행
 * - 장비별 뷰포인트 프리셋 데이터는 assets/json/matterport/*.json에서 로드
 * - 08-viewpoint-preset 프로젝트의 handleApplyPreset 로직 기반
 */
export class ViewpointPresetService {
  private static instance: ViewpointPresetService;
  // 2026.02.11 : 박은정 : Matterport SDK 인스턴스 (초기화 시 설정)
  private sdk: any = null;
  // 2026.02.11 : 박은정 : 장비 데이터 서비스 (뷰포인트 프리셋 조회용)
  private equipmentDataService = EquipmentDataService.getInstance();

  private constructor() {}

  // 2026.02.11 : 박은정 : 싱글톤 패턴
  static getInstance(): ViewpointPresetService {
    if (!ViewpointPresetService.instance) {
      ViewpointPresetService.instance = new ViewpointPresetService();
    }
    return ViewpointPresetService.instance;
  }

  // 2026.02.11 : 박은정 : SDK 인스턴스 설정 (Main.tsx componentDidMount에서 호출)
  initialize(sdk: any): void {
    this.sdk = sdk;
  }

  // 2026.02.11 : 박은정 : SDK 연결 여부 확인
  isInitialized(): boolean {
    return this.sdk !== null;
  }

  /**
   * 2026.02.11 : 박은정 : 장비 ID로 뷰포인트 프리셋 적용
   * 1단계: 해당 장비 Sweep으로 이동
   * 2단계: 카메라 회전(방향) 설정 (pitch / yaw)
   * 3단계: 줌 레벨 설정
   *
   * @param equipmentId 장비 ID (예: 'heat-exchanger')
   * @param options 전환 옵션 (선택)
   * @returns 적용 성공 여부
   */
  async applyPresetByEquipmentId(
    equipmentId: string,
    options?: ViewpointApplyOptions,
  ): Promise<boolean> {
    if (!this.sdk) {
      // 2026.02.11 : 박은정 : 디버깅 로그 제거
      return false;
    }

    const sweepId = this.equipmentDataService.getSweepId(equipmentId);
    const viewpoint = this.equipmentDataService.getViewpointPreset(equipmentId);

    if (!sweepId) {
      // 2026.02.11 : 박은정 : 디버깅 로그 제거
      return false;
    }

    if (!viewpoint) {
      // 2026.02.11 : 박은정 : 뷰포인트 프리셋이 없으면 Sweep 이동만 수행
      return this.moveToSweep(sweepId, options);
    }

    return this.applyPreset(sweepId, viewpoint, options);
  }

  /**
   * 2026.02.11 : 박은정 : Sweep 이동 + 뷰포인트 프리셋 적용 (내부 메서드)
   * 08-viewpoint-preset의 handleApplyPreset 로직 기반
   *
   * @param sweepId 이동할 Sweep ID
   * @param preset 적용할 뷰포인트 프리셋 (rotation + zoom)
   * @param options 전환 옵션 (선택)
   */
  private async applyPreset(
    sweepId: string,
    preset: ViewpointPreset,
    options?: ViewpointApplyOptions,
  ): Promise<boolean> {
    const opts = { ...DEFAULT_OPTIONS, ...options };

    try {
      // 2026.02.11 : 박은정 : 1단계 – 해당 Sweep으로 이동
      await this.sdk.Sweep.moveTo(sweepId, {
        transition: this.sdk.Sweep.Transition[opts.sweepTransition] || this.sdk.Sweep.Transition.FLY,
        transitionTime: opts.sweepTransitionTime,
      });

      // 2026.02.11 : 박은정 : 2단계 – 카메라 회전(방향) 설정 (pitch / yaw)
      await this.sdk.Camera.setRotation(
        { x: preset.rotation.x, y: preset.rotation.y },
        { speed: opts.rotationSpeed },
      );

      // 2026.02.11 : 박은정 : 3단계 – 줌 레벨 설정
      if (preset.zoom > 0) {
        await this.sdk.Camera.zoomTo(preset.zoom);
      } else {
        await this.sdk.Camera.zoomReset();
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * 2026.02.11 : 박은정 : Sweep 이동만 수행 (뷰포인트 프리셋 없을 때)
   *
   * @param sweepId 이동할 Sweep ID
   * @param options 전환 옵션 (선택)
   */
  private async moveToSweep(
    sweepId: string,
    options?: ViewpointApplyOptions,
  ): Promise<boolean> {
    const opts = { ...DEFAULT_OPTIONS, ...options };

    try {
      await this.sdk.Sweep.moveTo(sweepId, {
        transition: this.sdk.Sweep.Transition[opts.sweepTransition] || this.sdk.Sweep.Transition.FLY,
        transitionTime: opts.sweepTransitionTime,
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * 2026.02.11 : 박은정 : 줌 리셋
   */
  async resetZoom(): Promise<void> {
    if (!this.sdk) return;
    try {
      await this.sdk.Camera.zoomReset();
    } catch (error) {
      // 에러 무시
    }
  }
}

